import { Component } from '@theme/component';

const SESSION_KEY_PREFIX = 'fvw:dismissed:';

/**
 * @typedef {Object} FloatingVideoWidgetRefs
 * @property {HTMLElement} bubble
 * @property {HTMLElement} panel
 */

/**
 * A floating video bubble for the product page. Autoplays a muted preview in the
 * collapsed bubble, expands into a larger video with sound on click, and can be
 * dismissed for the rest of the browser session.
 *
 * @extends {Component<FloatingVideoWidgetRefs>}
 */
class FloatingVideoWidget extends Component {
  requiredRefs = ['bubble', 'panel'];

  connectedCallback() {
    super.connectedCallback();

    if (this.#isDismissed()) {
      this.hidden = true;
      return;
    }

    document.addEventListener('keydown', this.#onKeydown);
    this.#playPreview();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown);
  }

  expand() {
    const { panel } = this.refs;
    const bubbleVideo = this.querySelector('.fvw-bubble__video');
    const panelVideo = this.querySelector('.fvw-panel__video');

    if (bubbleVideo instanceof HTMLVideoElement) bubbleVideo.pause();

    panel.hidden = false;
    this.setAttribute('expanded', '');

    if (panelVideo instanceof HTMLVideoElement) {
      panelVideo.currentTime = 0;
      panelVideo.muted = false;
      panelVideo.play?.().catch(() => {
        // Autoplay with sound can be blocked; fall back to a muted play so the
        // shopper still sees motion and can unmute via the native controls.
        panelVideo.muted = true;
        panelVideo.play?.().catch(() => {});
      });
    }
  }

  collapse() {
    const { panel } = this.refs;
    const panelVideo = this.querySelector('.fvw-panel__video');

    if (panelVideo instanceof HTMLVideoElement) panelVideo.pause();

    panel.hidden = true;
    this.removeAttribute('expanded');
    this.#playPreview();
  }

  async share() {
    const url = window.location.href;
    const title = document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        // The share sheet can be dismissed by the shopper; nothing to do.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      // Clipboard access can be blocked (permissions, insecure context, etc.);
      // there is no further fallback worth adding for a "nice to have" action.
    }
  }

  addToCart() {
    const selector = this.dataset.cartFormSelector || '[data-type="add-to-cart-form"]';
    const form = document.querySelector(selector);

    if (form instanceof HTMLFormElement) form.requestSubmit();

    this.collapse();
  }

  dismiss() {
    const panelVideo = this.querySelector('.fvw-panel__video');
    const bubbleVideo = this.querySelector('.fvw-bubble__video');

    if (panelVideo instanceof HTMLVideoElement) panelVideo.pause();
    if (bubbleVideo instanceof HTMLVideoElement) bubbleVideo.pause();

    this.hidden = true;

    const key = this.#storageKey;
    if (!key) return;

    try {
      sessionStorage.setItem(key, '1');
    } catch (error) {
      // Storage unavailable (private browsing, etc.) — the widget simply won't
      // persist the dismissal across page loads for this visitor.
    }
  }

  get #storageKey() {
    const id = this.dataset.productId;
    return id ? `${SESSION_KEY_PREFIX}${id}` : null;
  }

  #isDismissed() {
    const key = this.#storageKey;
    if (!key) return false;

    try {
      return sessionStorage.getItem(key) === '1';
    } catch (error) {
      return false;
    }
  }

  #playPreview() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const bubbleVideo = this.querySelector('.fvw-bubble__video');
    if (bubbleVideo instanceof HTMLVideoElement) bubbleVideo.play?.().catch(() => {});
  }

  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape' && this.hasAttribute('expanded')) this.collapse();
  };
}

if (!customElements.get('floating-video-widget')) {
  customElements.define('floating-video-widget', FloatingVideoWidget);
}
